import { useCallback, useMemo, useState } from "react";
import type {
  ApproveOrderPaymentRequest,
  OrderPaymentProofUrlItem,
  PaymentReviewRejectReasonCode,
  RejectOrderPaymentRequest,
  SellerOrderDetail,
} from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useOrderByIdQuery } from "@/modules/orders/services/useOrderByIdQuery";
import { useOrderProofQuery } from "@/modules/orders/services/useOrderProofQuery";
import { useApproveOrderPaymentMutation } from "@/modules/orders/services/useApproveOrderPaymentMutation";
import { useRejectOrderPaymentMutation } from "@/modules/orders/services/useRejectOrderPaymentMutation";

type Props = {
  isOpen: boolean;
  storeSlug: string;
  orderId: string | null;
  onSuccess: (order: SellerOrderDetail) => void;
};

function parseMutationErrorCode(caughtError: unknown): string {
  if (!(caughtError instanceof ApiJsonClientError)) {
    return "UNKNOWN";
  }
  if (caughtError.code) {
    return caughtError.code;
  }
  if (caughtError.status === 409) {
    return "STALE_STATUS";
  }
  if (caughtError.message.trim().length > 0) {
    return caughtError.message.trim();
  }
  return "UNKNOWN";
}

export function usePaymentReviewState({
  isOpen,
  storeSlug,
  orderId,
  onSuccess,
}: Props) {
  const activeOrderId = isOpen && orderId ? orderId : "";
  const orderQuery = useOrderByIdQuery({
    storeSlug,
    orderId: activeOrderId,
  });
  const proofQuery = useOrderProofQuery({
    storeSlug,
    orderId: activeOrderId,
  });
  const { mutateAsync: approveOrderPayment } = useApproveOrderPaymentMutation();
  const { mutateAsync: rejectOrderPayment } = useRejectOrderPaymentMutation();
  const [order, setOrder] = useState<SellerOrderDetail | null>(null);
  const [proofItems, setProofItems] = useState<OrderPaymentProofUrlItem[]>([]);
  const [selectedProofIndex, setSelectedProofIndex] = useState(0);
  const [approvedAmountInput, setApprovedAmountInput] = useState("");
  const [reasonCode, setReasonCode] = useState<PaymentReviewRejectReasonCode | "">(
    "",
  );
  const [note, setNote] = useState("");
  const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [isConflict, setIsConflict] = useState(false);

  const hasProof = proofItems.length > 0;
  const buyerEnteredAmount = order?.paidAmount ?? 0;
  const requiredAmount = order?.requiredAmount ?? 0;
  const approvedAmount = Number(approvedAmountInput.trim());
  const hasValidApprovedAmount =
    approvedAmountInput.trim().length > 0 &&
    Number.isFinite(approvedAmount) &&
    approvedAmount >= 0;

  // Reset the review state during render whenever the dialog opens/closes or
  // switches orders (previous-value comparison pattern — replaces the former
  // setState-in-effect resets). Submission flags are only cleared on close so
  // an in-flight submit isn't visually "un-pended" by a re-render while open.
  const [prevActiveOrderId, setPrevActiveOrderId] = useState(activeOrderId);
  if (activeOrderId !== prevActiveOrderId) {
    setPrevActiveOrderId(activeOrderId);
    setOrder(null);
    setProofItems([]);
    setSelectedProofIndex(0);
    setApprovedAmountInput("");
    setReasonCode("");
    setNote("");
    setErrorCode(null);
    setIsConflict(false);
    if (!activeOrderId) {
      setIsSubmittingApprove(false);
      setIsSubmittingReject(false);
    }
  }

  // Mirror the order query result into local state during render (same
  // previous-value comparison pattern; the local copy exists because mutations
  // also overwrite `order` on success).
  const [prevOrderSync, setPrevOrderSync] = useState<{
    orderId: string;
    data: typeof orderQuery.data;
    error: typeof orderQuery.error;
  }>({ orderId: "", data: undefined, error: null });
  if (
    prevOrderSync.orderId !== activeOrderId ||
    prevOrderSync.data !== orderQuery.data ||
    prevOrderSync.error !== orderQuery.error
  ) {
    setPrevOrderSync({
      orderId: activeOrderId,
      data: orderQuery.data,
      error: orderQuery.error,
    });
    if (activeOrderId) {
      if (orderQuery.error) {
        setOrder(null);
        setProofItems([]);
        setErrorCode("PAYMENT_REVIEW_LOAD_ERROR");
      } else if (orderQuery.data) {
        const currentOrder = orderQuery.data;
        setOrder(currentOrder);
        // Default the approved amount to what the order expects — the deposit
        // when there is one, otherwise the (discounted) order total — not the
        // amount the buyer entered, so the seller doesn't auto-approve an
        // over/under payment.
        const expectedAmount =
          currentOrder.requiredAmount > 0
            ? currentOrder.requiredAmount
            : currentOrder.totalAmount;
        setApprovedAmountInput((previousInput) =>
          previousInput.trim().length > 0
            ? previousInput
            : String(expectedAmount),
        );
        if (currentOrder.status !== "pending_payment_verification") {
          setIsConflict(true);
          setErrorCode("STALE_STATUS");
        }
      }
    }
  }

  // Mirror the proof query result into local state during render (same
  // pattern as the order sync above).
  const [prevProofSync, setPrevProofSync] = useState<{
    orderId: string;
    data: typeof proofQuery.data;
    error: typeof proofQuery.error;
  }>({ orderId: "", data: undefined, error: null });
  if (
    prevProofSync.orderId !== activeOrderId ||
    prevProofSync.data !== proofQuery.data ||
    prevProofSync.error !== proofQuery.error
  ) {
    setPrevProofSync({
      orderId: activeOrderId,
      data: proofQuery.data,
      error: proofQuery.error,
    });
    if (activeOrderId) {
      if (!proofQuery.error && proofQuery.data) {
        setProofItems(proofQuery.data.items ?? []);
      } else if (
        proofQuery.error instanceof ApiJsonClientError &&
        proofQuery.error.status === 404
      ) {
        setProofItems([]);
      } else if (proofQuery.error) {
        setProofItems([]);
        setErrorCode("PAYMENT_REVIEW_LOAD_ERROR");
      }
    }
  }

  const hasUnsavedChanges = useMemo(() => {
    if (!order) {
      return false;
    }
    if (approvedAmountInput.trim() !== String(order.paidAmount)) {
      return true;
    }
    if (reasonCode !== "") {
      return true;
    }
    return note.trim().length > 0;
  }, [approvedAmountInput, note, order, reasonCode]);

  const submitApprove = useCallback(async () => {
    if (!orderId || !hasValidApprovedAmount) {
      setErrorCode("INVALID_APPROVED_AMOUNT");
      return false;
    }
    if (!hasProof) {
      setErrorCode("PAYMENT_PROOF_REQUIRED");
      return false;
    }
    if (!storeSlug) {
      setErrorCode("UNKNOWN");
      return false;
    }
    setIsSubmittingApprove(true);
    setErrorCode(null);
    try {
      const payload: ApproveOrderPaymentRequest = {
        approvedAmount,
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      const updated = await approveOrderPayment({
        storeSlug,
        orderId,
        ...payload,
      });
      setOrder(updated);
      onSuccess(updated);
      return true;
    } catch (caughtError) {
      const code = parseMutationErrorCode(caughtError);
      setErrorCode(code);
      if (code === "STALE_STATUS") {
        setIsConflict(true);
      }
      return false;
    } finally {
      setIsSubmittingApprove(false);
    }
  }, [
    approveOrderPayment,
    approvedAmount,
    hasProof,
    hasValidApprovedAmount,
    note,
    onSuccess,
    orderId,
    storeSlug,
  ]);

  const submitReject = useCallback(async () => {
    if (!orderId) {
      return false;
    }
    if (!reasonCode) {
      setErrorCode("PAYMENT_REJECT_REASON_REQUIRED");
      return false;
    }
    if (!storeSlug) {
      setErrorCode("UNKNOWN");
      return false;
    }
    setIsSubmittingReject(true);
    setErrorCode(null);
    try {
      const payload: RejectOrderPaymentRequest = {
        reasonCode,
        ...(note.trim() ? { note: note.trim() } : {}),
      };
      const updated = await rejectOrderPayment({
        storeSlug,
        orderId,
        ...payload,
      });
      setOrder(updated);
      onSuccess(updated);
      return true;
    } catch (caughtError) {
      const code = parseMutationErrorCode(caughtError);
      setErrorCode(code);
      if (code === "STALE_STATUS") {
        setIsConflict(true);
      }
      return false;
    } finally {
      setIsSubmittingReject(false);
    }
  }, [note, onSuccess, orderId, reasonCode, rejectOrderPayment, storeSlug]);

  const isLoading =
    Boolean(isOpen && orderId) && (orderQuery.isLoading || proofQuery.isLoading);
  const isSubmitting = isSubmittingApprove || isSubmittingReject;

  return {
    order,
    proofItems,
    selectedProofIndex,
    setSelectedProofIndex,
    approvedAmountInput,
    setApprovedAmountInput,
    reasonCode,
    setReasonCode,
    note,
    setNote,
    isLoading,
    isSubmitting,
    isConflict,
    errorCode,
    setErrorCode,
    requiredAmount,
    buyerEnteredAmount,
    approvedAmount,
    hasProof,
    hasValidApprovedAmount,
    hasUnsavedChanges,
    submitApprove,
    submitReject,
  };
}
